'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MdSearch } from 'react-icons/md';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Input } from '@/components/ui/input/Input';
import { RateSelectionTable } from '../change-discount/RateSelectionTable';
import type { RateRow } from '../change-discount/ChangeDiscount.types';

/**
 * Shared presentational picker sheet for choosing a discount program.
 * Used by both the scan-result flow (caller navigates after selection)
 * and the validate-page "Change discount" flow (caller swaps in place).
 *
 * ## Invariants:
 * - Does NOT apply the discount itself — delegates to `onSelect`.
 * - Filtering is local/client-side only; no API calls are made here.
 */
interface DiscountProgramPickerSheetProps {
  open: boolean;
  onClose: () => void;
  rates: RateRow[];
  loading: boolean;
  /** Called with the selected specialRateId; caller decides navigate vs in-place swap. */
  onSelect: (specialRateId: string) => void;
  /**
   * z-index overrides forwarded to the underlying BottomSheet. Pass elevated
   * values when this picker opens on top of another BottomSheet (e.g. inside
   * FindSessionModal) so its backdrop dims the sheet beneath instead of
   * rendering below it and merging the two popups.
   */
  zIndex?: { backdrop?: string; sheet?: string };
}

export function DiscountProgramPickerSheet({
  open,
  onClose,
  rates,
  loading,
  onSelect,
  zIndex,
}: DiscountProgramPickerSheetProps) {
  const t = useTranslations('ParkingValidation');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    if (!query.trim()) return rates;
    const q = query.toLowerCase();
    return rates.filter((r) => r.specialRateName.toLowerCase().includes(q));
  }, [rates, query]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t('chooseDiscountProgram')}
      bodyClassName="tw:p-base"
      zIndex={zIndex}
    >
      <div className="tw:flex tw:flex-col tw:gap-base" data-cy="discount-picker">
        <Input
          value={query}
          onChange={(val) => setQuery(val ?? '')}
          placeholder={t('searchPlaceholder')}
          leftIcon={<MdSearch size={20} aria-hidden="true" />}
          hideBottomPadding
          data-cy="discount-picker-search"
        />
        <RateSelectionTable rates={filtered} onSelectRate={onSelect} loading={loading} />
      </div>
    </BottomSheet>
  );
}
